import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { formatUsdcBaseUnits } from "@hybrid-escrow/shared";
import { adminApiClient } from "../../lib/adminApiClient";
import { queryKeys } from "../../lib/queryClient";
import { ListingStatusBadge } from "../../components/StatusBadge";
import { ErrorBanner, LoadingState, describeError } from "../../components/States";

export function AdminListingsPage() {
  const queryClient = useQueryClient();
  const [actionError, setActionError] = useState<string | null>(null);
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: queryKeys.adminListings,
    queryFn: () => adminApiClient.listListings({ limit: 50 }),
  });

  const suspend = useMutation({
    mutationFn: (listingId: string) => adminApiClient.suspendListing(listingId),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: queryKeys.adminListings }),
    onError: (err) => setActionError(describeError(err)),
  });

  const del = useMutation({
    mutationFn: (listingId: string) => adminApiClient.deleteListing(listingId),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: queryKeys.adminListings }),
    onError: (err) => setActionError(describeError(err)),
  });

  return (
    <div className="stack">
      <h1>Listings</h1>

      {isLoading && <LoadingState label="Loading listings…" />}
      {isError && <ErrorBanner error={error} onRetry={() => void refetch()} />}
      {actionError && (
        <p className="field-error-text" role="alert">
          {actionError}
        </p>
      )}

      {data && (
        <div style={{ overflowX: "auto" }}>
          <table className="admin-table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Seller</th>
                <th>Price</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((l) => (
                <tr key={l.id}>
                  <td>{l.title}</td>
                  <td>{l.sellerUsername ?? l.sellerId.slice(0, 8)}</td>
                  <td>${formatUsdcBaseUnits(BigInt(l.priceBaseUnits))}</td>
                  <td>
                    <ListingStatusBadge status={l.status} />
                  </td>
                  <td>
                    <div className="row">
                      {l.status !== "ARCHIVED" && (
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm"
                          disabled={suspend.isPending}
                          onClick={() => suspend.mutate(l.id)}
                        >
                          Suspend
                        </button>
                      )}
                      <button
                        type="button"
                        className="btn btn-danger btn-sm"
                        disabled={del.isPending}
                        onClick={() => {
                          if (window.confirm(`Permanently delete "${l.title}"? This can't be undone.`)) {
                            del.mutate(l.id);
                          }
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {data.items.length === 0 && <p className="muted">No listings found.</p>}
        </div>
      )}
    </div>
  );
}
